import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

// Google / Microsoft OAuth is hidden for now (providers not yet configured on
// the self-hosted backend). Flip to `true` to restore the "or continue with"
// buttons — all social-login logic below is kept intact.
const SOCIAL_LOGIN_ENABLED = false;

const authSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const Auth = () => {
  const { user, loading, tenantSlug, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // After user is set, check MFA requirement before redirecting.
  // We hold the redirect here until the async check resolves.
  const [mfaChecking, setMfaChecking] = useState(false);
  const [mfaChecked, setMfaChecked] = useState(false);

  useEffect(() => {
    if (!user || mfaChecked || mfaChecking) return;

    setMfaChecking(true);
    (async () => {
      try {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const hasVerifiedTotp = factors?.totp?.some((f) => f.status === 'verified') ?? false;

        if (!hasVerifiedTotp) {
          const { data: setting } = await supabase
            .from('tenant_settings')
            .select('setting_value')
            .eq('setting_key', 'mfa_required')
            .maybeSingle();

          const required = (setting?.setting_value as { enabled?: boolean } | null)?.enabled ?? false;
          if (required) {
            setMfaChecked(true);
            setMfaChecking(false);
            navigate('/mfa-setup', { replace: true });
            return;
          }
        }
      } catch {
        // fail open — proceed to normal destination
      }

      setMfaChecked(true);
      setMfaChecking(false);
      navigate(tenantSlug ? `/${tenantSlug}/app` : '/onboarding', { replace: true });
    })();
  }, [user, mfaChecked, mfaChecking, tenantSlug, navigate]);

  if (loading || mfaChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // User is set but MFA check hasn't started yet — render nothing momentarily
  if (user) return null;

  const handleSocialLogin = async (provider: 'google' | 'azure') => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/onboarding`,
      },
    });

    if (error) {
      toast({
        title: 'Login Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async (mode: 'login' | 'signup') => {
    const validation = authSchema.safeParse({ email, password });
    if (!validation.success) {
      toast({
        title: 'Validation Error',
        description: validation.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    if (mode === 'signup' && !consentChecked) {
      toast({
        title: 'Consent required',
        description: 'Please confirm you agree to the data processing terms to create an account.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    const { error } = mode === 'login'
      ? await signIn(email, password)
      : await signUp(email, password);

    if (error) {
      toast({
        title: mode === 'login' ? 'Login Failed' : 'Sign Up Failed',
        description: error.message,
        variant: 'destructive',
      });
    } else if (mode === 'signup') {
      // Best-effort — never blocks the signup flow. If email confirmation
      // is required, the session isn't authenticated yet and this insert
      // may fail silently; that's acceptable (matches logAIOutput's
      // fire-and-forget pattern elsewhere in this codebase).
      supabase.from('consent_log').insert({
        subject_email: email,
        purpose: 'platform_signup',
        granted: true,
      }).then(({ error: consentError }) => {
        if (consentError) console.warn('[auth] consent log insert failed:', consentError.message);
      });

      toast({
        title: 'Account Created',
        description: 'You can now log in with your credentials.',
      });
    }
    setIsSubmitting(false);
  };

  const handleForgotPassword = async () => {
    const validation = z.string().email('Please enter a valid email address').safeParse(email);
    if (!validation.success) {
      toast({ title: 'Validation Error', description: validation.error.errors[0].message, variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    setIsSubmitting(false);

    if (error) {
      toast({ title: 'Failed to send reset email', description: error.message, variant: 'destructive' });
      return;
    }

    setResetSent(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="w-full max-w-md border-border/50 shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src="/masira-logo-mark.svg" alt="Masira Logo" className="h-12 w-12" />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome to Masira</CardTitle>
          <CardDescription>
            AI-first Project Management Operating System
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              {showForgotPassword ? (
                resetSent ? (
                  <>
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Check your email for a link to reset your password.
                    </p>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => { setShowForgotPassword(false); setResetSent(false); }}
                    >
                      Back to sign in
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Enter your email and we'll send you a link to reset your password.
                    </p>
                    <Button className="w-full" onClick={handleForgotPassword} disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                      Send reset link
                    </Button>
                    <Button variant="ghost" className="w-full" onClick={() => setShowForgotPassword(false)}>
                      Back to sign in
                    </Button>
                  </>
                )
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setResetSent(false); }}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Forgot password?
                  </button>
                  <Button
                    className="w-full"
                    onClick={() => handleSubmit('login')}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                    Sign In
                  </Button>
                </>
              )}
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email</Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-password">Password</Label>
                <Input
                  id="signup-password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-border"
                  disabled={isSubmitting}
                />
                <span>
                  I agree to Masira processing my personal data to provide this service, in accordance
                  with the Saudi PDPL.
                </span>
              </label>
              <Button
                className="w-full"
                onClick={() => handleSubmit('signup')}
                disabled={isSubmitting || !consentChecked}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                Create Account
              </Button>
            </TabsContent>
          </Tabs>

          {SOCIAL_LOGIN_ENABLED && (
          <>
          <div className="relative my-6">
            <Separator />
            <span className="absolute start-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
              or continue with
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => handleSocialLogin('google')}
              className="w-full"
            >
              <svg className="me-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSocialLogin('azure')}
              className="w-full"
            >
              <svg className="me-2 h-4 w-4" viewBox="0 0 23 23">
                <path fill="#f35325" d="M1 1h10v10H1z" />
                <path fill="#81bc06" d="M12 1h10v10H12z" />
                <path fill="#05a6f0" d="M1 12h10v10H1z" />
                <path fill="#ffba08" d="M12 12h10v10H12z" />
              </svg>
              Microsoft
            </Button>
          </div>
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
